/**
 * Fullscreen power for the whole client — one state, three faces: the HUD's ⛶
 * button (next to the music power), the menu's "Full screen" row, and the
 * auto-enter offered when a match is about to begin on a phone.
 *
 * Everything is best-effort: desktop browsers (Chrome/Edge/Firefox) and
 * Android honor the Fullscreen API, iPhone Safari refuses pages outright, so
 * callers check `fullscreenSupported()` before showing a control and every
 * request swallows its own rejection. State settles asynchronously — the
 * standard `fullscreenchange` event is the single source of truth, and
 * `onFullscreenChange` is how the HUD button and the menu row stay in sync
 * when the other one (or Esc) flips it.
 */

/** Paint callbacks fired on every settled fullscreen transition. */
const listeners = new Set<() => void>();

/**
 * The user took fullscreen back (our toggle's exit side, or Esc/home
 * gesture): stop auto-offering it on later match starts. A manual re-enter
 * clears the flag — asking twice after they chose it again would nag.
 */
let declined = false;
/** An auto-enter is in flight; if it lands, a later exit means "declined". */
let pendingAuto = false;
let autoEntered = false;

export function fullscreenSupported(): boolean {
  return typeof document !== 'undefined' && typeof document.documentElement.requestFullscreen === 'function';
}

export function isFullscreen(): boolean {
  const d = document as Document & { webkitFullscreenElement?: Element };
  return Boolean(document.fullscreenElement ?? d.webkitFullscreenElement);
}

/** Subscribe to settled transitions (idempotent — one sub per painter). */
export function onFullscreenChange(cb: () => void): void {
  listeners.add(cb);
}

function changed(): void {
  if (isFullscreen()) {
    if (pendingAuto) {
      autoEntered = true;
      pendingAuto = false;
    }
  } else {
    pendingAuto = false;
    if (autoEntered) declined = true;
  }
  for (const cb of listeners) cb();
}

/** Enter when out, exit when in — the one toggle every control calls. */
export function toggleFullscreen(): void {
  if (!fullscreenSupported()) return;
  if (isFullscreen()) {
    declined = true;
    void document.exitFullscreen().catch(() => {});
  } else {
    declined = false;
    void document.documentElement.requestFullscreen().catch(() => {});
  }
}

/**
 * Auto-enter before a match on phones only: the request rides the tap that
 * started the match (the Fullscreen API needs a fresh user gesture), skips
 * desktops (windows stay windows unless asked), skips an installed app
 * (already chrome-free), and never re-offers after a deliberate exit.
 */
export function enterFullscreenIfUseful(): void {
  if (declined || pendingAuto || !fullscreenSupported() || isFullscreen()) return;
  if (!matchMedia('(pointer: coarse)').matches) return;
  if (matchMedia('(display-mode: standalone)').matches) return;
  pendingAuto = true;
  document.documentElement.requestFullscreen().catch(() => {
    pendingAuto = false;
  });
}

if (typeof document !== 'undefined') {
  document.addEventListener('fullscreenchange', changed);
  // iPad Safari still fires the prefixed event for the element API.
  document.addEventListener('webkitfullscreenchange', changed);
}
